import React, { createContext, useContext, useState, useEffect } from 'react';
import { Subject } from '../types/subject';
import { getSubjects } from '../services/subjectService';
import toast from 'react-hot-toast';
import { onSnapshot, collection } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface SubjectsContextType {
  subjects: Subject[];
  filteredSubjects: Subject[];
  loading: boolean;
  selectedType: string;
  setSelectedType: (type: string) => void;
  selectedChapter: string;
  setSelectedChapter: (chapter: string) => void;
  selectedYear: string;
  setSelectedYear: (year: string) => void;
  selectedSession: string;
  setSelectedSession: (session: string) => void;
  selectedLocation: string;
  setSelectedLocation: (location: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  refreshSubjects: () => Promise<void>;
  isOffline: boolean;
  error: string | null;
}

const SubjectsContext = createContext<SubjectsContextType | undefined>(undefined);

export const useSubjects = () => {
  const context = useContext(SubjectsContext);
  if (context === undefined) {
    throw new Error('useSubjects must be used within a SubjectsProvider');
  }
  return context;
};

// Ordre de priorité pour les types d'épreuve
const typeOrder = {
  'dissertation': 1,
  'ec1': 2,
  'ec2': 3,
  'ec3': 4
};

export const SubjectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Monitor connection state
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      toast.success('Connexion rétablie');
      refreshSubjects();
    };

    const handleOffline = () => {
      setIsOffline(true);
      toast.error('Mode hors ligne - Les données peuvent ne pas être à jour');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Set up real-time listener for subjects
    const unsubscribe = onSnapshot(
      collection(db, 'subjects'),
      (snapshot) => {
        const updatedSubjects = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Subject[];
        setSubjects(updatedSubjects);
        setLoading(false);
      },
      (error) => {
        console.error('Snapshot error:', error);
        setError('Erreur lors de la synchronisation des données');
        setLoading(false);
      }
    );

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, []);

  const refreshSubjects = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const fetchedSubjects = await getSubjects({
        type: selectedType,
        chapter: selectedChapter,
        year: selectedYear,
        session: selectedSession,
        location: selectedLocation,
        searchQuery: searchQuery
      });
      
      setSubjects(fetchedSubjects);
    } catch (error: any) {
      console.error('Error refreshing subjects:', error);
      setError(error.message || 'Une erreur est survenue lors du chargement des sujets');
      if (!isOffline) {
        toast.error(error.message || 'Erreur lors du chargement des sujets');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonction de tri personnalisée
  const sortSubjects = (a: Subject, b: Subject) => {
    // D'abord par année (décroissant)
    if (a.year !== b.year) {
      return parseInt(b.year) - parseInt(a.year);
    }

    // Ensuite par centre d'examen
    if (a.location !== b.location) {
      return a.location.localeCompare(b.location);
    }

    // Ensuite par session
    if (a.session !== b.session) {
      const sessionOrder = {
        'normale': 1,
        'remplacement': 2,
        'candidat-libre': 3
      };
      return (sessionOrder[a.session as keyof typeof sessionOrder] || 99) - 
             (sessionOrder[b.session as keyof typeof sessionOrder] || 99);
    }

    // Enfin par type d'épreuve selon l'ordre défini
    return (typeOrder[a.type as keyof typeof typeOrder] || 99) - 
           (typeOrder[b.type as keyof typeof typeOrder] || 99);
  };

  // Filter subjects based on selected filters
  const filteredSubjects = subjects
    .filter(subject => {
      if (selectedType && subject.type !== selectedType) return false;
      if (selectedChapter && subject.chapter !== selectedChapter) return false;
      if (selectedYear && subject.year !== selectedYear) return false;
      if (selectedSession && subject.session !== selectedSession) return false;
      if (selectedLocation && subject.location !== selectedLocation) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          subject.title.toLowerCase().includes(query) ||
          subject.chapter.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort(sortSubjects);

  const value = {
    subjects,
    filteredSubjects,
    loading,
    selectedType,
    setSelectedType,
    selectedChapter,
    setSelectedChapter,
    selectedYear,
    setSelectedYear,
    selectedSession,
    setSelectedSession,
    selectedLocation,
    setSelectedLocation,
    searchQuery,
    setSearchQuery,
    refreshSubjects,
    isOffline,
    error
  };

  return (
    <SubjectsContext.Provider value={value}>
      {children}
    </SubjectsContext.Provider>
  );
};